/**
 * Appointment Date Helpers
 *
 * Firestore read/write for per-module appointment dates on patient documents,
 * plus an append-only audit trail subcollection.
 *
 * Data architecture:
 *   - Current dates live on the patient document as a top-level map
 *     `appointmentDates.{shortKey}` (see VALID_MODULE_KEYS for the keys each
 *     pathway uses). Each entry stores the appointment
 *     as a calendar date string `YYYY-MM-DD` (not a Timestamp instant) so
 *     report rendering never shifts the day across London DST boundaries.
 *   - Change history lives in
 *     `patients/{patientId}/appointmentDateHistory/{autoId}`, following the
 *     same fire-and-forget, append-only pattern as `reportAuditHistory`.
 *
 * Why updateDoc (not updatePatientFirestore):
 *   1. Appointment saves are discrete user actions, not streaming edits
 *   2. Avoids merge conflicts with concurrent dataModel writes
 *   3. Avoids the 200ms debounce — saves feel instant
 *   4. Mirrors the archive/restore pattern in PatientArchiveDialog
 *
 * After any write, callers should invoke fetchPatientData(patientId) so the
 * UI reflects the update.
 */

import { format, parse } from 'date-fns';
import { emitAppointmentDateEvent, emitModuleCompleted } from '../analytics/appointmentEvents';
import { db, auth } from '../firebase';
import {
  doc,
  updateDoc,
  collection,
  addDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  deleteField,
} from 'firebase/firestore';

/**
 * Valid short keys for `appointmentDates.{key}`. This is the write-side
 * allow-list: `applyAppointmentDateChanges` rejects anything else, so adding
 * a pathway means adding its keys here.
 *
 *   - `ci`, `ssi`, `dd`, `pad` — ADHD (adult and CYP). Matches
 *     STAGE_SHORT_NAMES in PatientTable.js.
 *   - `ddi`, `obs` — Adult Autism. `ddi` is the single Diagnostic Developmental
 *     Interview appointment that covers BOTH the History and SSI stages (they
 *     are done in one sitting, so the overview shows one card and one date);
 *     `obs` is the Behavioural Observation. Autism reuses `dd` and `pad`.
 *     These match STAGE_TO_PROGRESS_KEY in constants/adultAutismStages.js and
 *     python-functions/functions/autism_stage_progress.py, so the pill the
 *     dashboard shows and the key the PDF pipeline reads are the same string.
 */
export const VALID_MODULE_KEYS = ['ci', 'ssi', 'dd', 'pad', 'ddi', 'obs'];

/**
 * Patient Overview card module key → appointmentDates short key.
 *
 * Shared by the AppointmentDateDialog and every Patient Overview that mounts
 * it, so the three pathways cannot drift. ADHD and Autism both use
 * `diagnosticDecision` / `postAssessmentDiscussion`, which is why those two
 * map to the same `dd` / `pad` keys regardless of pathway.
 */
export const MODULE_KEY_TO_SHORT_KEY = {
  // ADHD (adult + CYP)
  clinicalInterview: 'ci',
  semiStructuredInterview: 'ssi',
  // Adult Autism
  developmentalDiagnosticInterview: 'ddi',
  behaviouralObservation: 'obs',
  // Shared by ADHD and Autism
  diagnosticDecision: 'dd',
  postAssessmentDiscussion: 'pad',
};

/** Fallback display titles when the org config has no title for a module. */
export const SHORT_KEY_LABELS = {
  ci: 'Clinical Interview',
  ssi: 'Semi-Structured Interview',
  ddi: 'Diagnostic Developmental History',
  obs: 'Behavioural Observation',
  dd: 'Diagnostic Decision',
  pad: 'Post-Assessment Discussion',
};

/**
 * What kind of date each module records, for aria-labels and copy.
 *
 * Most modules are carried out in a patient-facing appointment, so their date
 * is an "appointment date". The Diagnostic Decision (ADHD) / MDT Review
 * (Autism) is made in the clinician's own time, not a patient appointment, so
 * it reads plainly as "date" for accuracy.
 */
export const SHORT_KEY_DATE_LABELS = {
  ci: 'appointment date',
  ssi: 'appointment date',
  ddi: 'appointment date',
  obs: 'appointment date',
  dd: 'date',
  pad: 'appointment date',
};

/** Strict calendar-date pattern: YYYY-MM-DD */
const CALENDAR_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Resolve a human-readable actor name for audit entries.
 * Many Auth users have a null displayName, so fall back to email / Unknown.
 */
function resolveActorName(user) {
  if (!user) return null;
  return user.displayName || user.email || 'Unknown User';
}

/**
 * Normalise a stored appointment date value to a YYYY-MM-DD string.
 *
 * Accepts:
 *   - calendar strings ("2026-07-18")
 *   - legacy Firestore Timestamp / Date values from earlier iterations
 *
 * Returns null when the value is missing or unparseable.
 */
export function toCalendarDateString(value) {
  if (!value) return null;

  if (typeof value === 'string') {
    // Already a calendar date, or an ISO datetime — take the date portion.
    const datePart = value.slice(0, 10);
    return CALENDAR_DATE_RE.test(datePart) ? datePart : null;
  }

  // Firestore Timestamp (live or serialised)
  let dateObj = null;
  if (typeof value.toDate === 'function') {
    dateObj = value.toDate();
  } else if (typeof value.seconds === 'number') {
    dateObj = new Date(value.seconds * 1000);
  } else if (value instanceof Date) {
    dateObj = value;
  }

  if (!dateObj || Number.isNaN(dateObj.getTime())) return null;

  // Format from local calendar components so we don't shift the day when the
  // Timestamp was constructed at local midnight during BST.
  const y = dateObj.getFullYear();
  const m = String(dateObj.getMonth() + 1).padStart(2, '0');
  const d = String(dateObj.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Format a YYYY-MM-DD appointment date for clinician-facing UI.
 * Parses as a local calendar day (never via `new Date('YYYY-MM-DD')`, which
 * is UTC midnight and can display as the previous day in western timezones).
 *
 * @param {string|null} calendarDate
 * @param {string} [pattern='EEE, d MMM yyyy']  e.g. "Thu, 16 Jan 2026"
 * @returns {string}
 */
export function formatAppointmentDateDisplay(calendarDate, pattern = 'EEE, d MMM yyyy') {
  const normalised = toCalendarDateString(calendarDate);
  if (!normalised) return '';
  const localDate = parse(normalised, 'yyyy-MM-dd', new Date());
  if (Number.isNaN(localDate.getTime())) return '';
  return format(localDate, pattern);
}

/**
 * Convert an instant (Timestamp / Date / ISO / {seconds}) to a YYYY-MM-DD
 * calendar day in Europe/London.
 *
 * Matches the report pipeline's timezone (format_date_with_weekday uses
 * Europe/London). Uses Intl — no date-fns-tz dependency.
 *
 * For already-normalised calendar strings this returns the date portion as-is
 * (calendar dates are timezone-free by definition).
 *
 * @param {*} value
 * @returns {string|null}
 */
export function toLondonCalendarDateString(value) {
  if (!value) return null;

  if (typeof value === 'string') {
    const datePart = value.slice(0, 10);
    // Pure calendar string — no timezone conversion needed.
    if (CALENDAR_DATE_RE.test(datePart) && value.length === 10) {
      return datePart;
    }
    // ISO datetime — convert the instant via London.
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return CALENDAR_DATE_RE.test(datePart) ? datePart : null;
    }
    return formatInstantAsLondonCalendarDay(parsed);
  }

  let dateObj = null;
  if (typeof value.toDate === 'function') {
    dateObj = value.toDate();
  } else if (typeof value.seconds === 'number') {
    dateObj = new Date(value.seconds * 1000);
  } else if (value instanceof Date) {
    dateObj = value;
  }

  if (!dateObj || Number.isNaN(dateObj.getTime())) return null;
  return formatInstantAsLondonCalendarDay(dateObj);
}

/**
 * Format a Date instant as YYYY-MM-DD in Europe/London.
 * en-CA yields an ISO-like year-month-day string.
 */
function formatInstantAsLondonCalendarDay(dateObj) {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/London',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(dateObj);

    const year = parts.find((p) => p.type === 'year')?.value;
    const month = parts.find((p) => p.type === 'month')?.value;
    const day = parts.find((p) => p.type === 'day')?.value;
    if (!year || !month || !day) return null;

    const result = `${year}-${month}-${day}`;
    return CALENDAR_DATE_RE.test(result) ? result : null;
  } catch {
    return null;
  }
}

/**
 * Resolve what the Patient Overview UI should show for a module's appointment
 * date. Mirrors the backend order in get_stage_assessment_date (Python is the
 * source of truth for PDF text; this is cosmetic parity only):
 *   1. Explicit human-set appointmentDates.{module}.date
 *   2. firstCompletedAt (London calendar day)
 *   3. lastCompletedAt (London calendar day)
 *
 * When the value comes from (2)/(3), isInferred is true so the card can mark
 * it as "estimated from completion" rather than a confirmed appointment.
 *
 * @param {Object} params
 * @param {*} [params.appointmentDate]
 * @param {*} [params.firstCompletedAt]
 * @param {*} [params.lastCompletedAt]
 * @returns {{ date: string|null, isInferred: boolean }}
 */
export function resolveDisplayAppointmentDate({
  appointmentDate = null,
  firstCompletedAt = null,
  lastCompletedAt = null,
} = {}) {
  const manual = toCalendarDateString(appointmentDate);
  if (manual) {
    return { date: manual, isInferred: false };
  }

  // Try each timestamp independently so a truthy-but-unconvertible value
  // (e.g. a corrupt Firestore map `{ _methodName: 'serverTimestamp' }` from
  // the stripUndefined bug) does not block the fallback to lastCompletedAt.
  const inferred = toLondonCalendarDateString(firstCompletedAt)
    || toLondonCalendarDateString(lastCompletedAt);
  if (inferred) {
    return { date: inferred, isInferred: true };
  }

  return { date: null, isInferred: false };
}

/**
 * Pick the timestamp a modular (Autism / CYP) PDF would print as the stage's
 * "Appointment Date" when no clinician-entered date exists.
 *
 * Why this exists
 * ---------------
 * The modular backend (`asd_car/render.py::resolve_assessment_date`) resolves
 * a stage's printed date in three tiers:
 *
 *   1. a clinician-entered `appointmentDates.{key}.date` on the patient header;
 *   2. the write-once `firstCompletedAt` on the stage's assessmentStages/{stage}
 *      doc (the day the clinician pressed Finish — parity with ADHD);
 *   3. the audit timestamps on the stage's own `reportContent/{stage}` document.
 *
 * This function is the frontend half of tier 3. The Autism overview reads tier
 * 2 directly off the stage doc and only falls back to this chain when that
 * stamp is missing, mirroring the Python order exactly so the "estimated" date
 * on a card is always what the PDF would print. If the Python order ever
 * changes, this must change with it.
 *
 * Tier 3 matters for patients whose stages were completed before Autism wrote
 * `firstCompletedAt` — they have report timestamps but no stamp — so legacy
 * cards keep showing the same estimate they always did. Note that on Autism
 * the first generation happens on section exit (well before Finish), so for
 * those legacy patients this chain is "the day the clinician started the
 * stage", which is why the Finish stamp is preferred whenever it exists.
 *
 * Candidate order (first present wins):
 *   1. `{prefix}ReportFirstGeneratedAt` — write-once, first LLM generation.
 *   2. `{prefix}ReportGeneratedAt`      — latest generation (older patients
 *                                         pre-date the write-once field).
 *   3. `{prefix}ReportFirstApprovedAt`  — final fallbacks for data generated
 *   4. `{prefix}ReportApprovedAt`         before the generated-at fields existed.
 *
 * The raw timestamp (Firestore Timestamp / Date / ISO / {seconds}) is returned
 * untouched so callers can feed it straight into `resolveDisplayAppointmentDate`
 * (which converts it to a London calendar day).
 *
 * @param {Object|null|undefined} reportContentDoc  The `reportContent/{stage}` document
 * @param {string} prefix  Stage status prefix, e.g. 'history', 'ssi', 'obs', 'dd', 'pad'
 * @returns {*} the first available timestamp value, or null
 */
export function resolveAutismReportDateAnchor(reportContentDoc, prefix) {
  if (!reportContentDoc || typeof reportContentDoc !== 'object' || !prefix) return null;

  const candidateFields = [
    `${prefix}ReportFirstGeneratedAt`,
    `${prefix}ReportGeneratedAt`,
    `${prefix}ReportFirstApprovedAt`,
    `${prefix}ReportApprovedAt`,
  ];

  for (const field of candidateFields) {
    const value = reportContentDoc[field];
    if (value) return value;
  }
  return null;
}

/**
 * Fire-and-forget audit entry when a module's first completion establishes an
 * inferred appointment date. Does NOT write appointmentDates and does NOT
 * bump appointmentDatesLastChangedAt — display-only inference stays intact.
 *
 * Guards (caller should pass these; helper is also defensive):
 *   - didWriteFirstCompleted: only on the true first-completion write
 *   - enabled: appointmentDates feature flag for the org
 *   - no clinician-entered date already on `appointmentDates.{moduleKey}` —
 *     checked here with one patient-document read (see below)
 *
 * Why the extra read
 * ------------------
 * The audit entry says "the appointment date for this module was auto-set to
 * today". That is only true if the overview is actually going to SHOW today
 * as the inferred date, which it does only when no manual date exists — a
 * clinician-entered date always wins (`resolveDisplayAppointmentDate`). A
 * clinician can type the appointment date on the Patient Overview before the
 * module is ever finished (booking ahead), so without this check the trail
 * would gain an `auto_set` row for a date nobody ever saw, right under the
 * `set` row the clinician did make. One `getDoc` on the patient header is the
 * cheapest way to keep the audit honest; it runs once per module per patient
 * (only on the write-once first completion) so the cost is negligible.
 *
 * Failure mode: if the read fails (offline, permissions) we append anyway and
 * log — a possibly-redundant audit row is far better than silently losing the
 * record of how the inferred date came about.
 *
 * The function stays synchronous from the caller's point of view (fire and
 * forget, like every other audit writer in this file); the returned promise
 * is only there so tests can await the chain.
 *
 * @param {Object} params
 * @param {string} params.patientId
 * @param {string} params.moduleKey  'ci'|'ssi'|'dd'|'pad'|'ddi'|'obs'
 * @param {boolean} params.didWriteFirstCompleted
 * @param {boolean} params.enabled
 * @param {string|null} [params.inferredDate]  YYYY-MM-DD; defaults to London "today"
 * @returns {Promise<void>|undefined}  resolves once the audit decision has been
 *   made (and the write issued, if any); undefined when guards short-circuit
 */
export function recordInferredAppointmentDateOnCompletion({
  patientId,
  moduleKey,
  didWriteFirstCompleted,
  enabled,
  inferredDate = null,
}) {
  if (!patientId || !VALID_MODULE_KEYS.includes(moduleKey)) return;

  // Analytics: the module's FIRST completion, mirrored here because every
  // module-completing step already calls this helper with the write-once flag.
  // Deliberately before the feature gate below - the inferred-appointment
  // behaviour is optional, the fact that the module completed is not.
  if (didWriteFirstCompleted) {
    emitModuleCompleted({ patientId, moduleKey });
  }

  if (!didWriteFirstCompleted || !enabled) return;

  const newDate = inferredDate || toLondonCalendarDateString(new Date());
  if (!newDate) return;

  const append = () => appendAppointmentDateAudit(patientId, {
    module: moduleKey,
    action: 'auto_set',
    trigger: 'moduleCompletion',
    previousDate: null,
    newDate,
  });

  return getDoc(doc(db, 'patients', patientId))
    .then((snap) => {
      const manualDate = snap?.data?.()?.appointmentDates?.[moduleKey]?.date;
      if (manualDate) {
        // A clinician-entered date is already on the header, so the inferred
        // date is never displayed and must not be recorded as "auto-set".
        return;
      }
      append();
    })
    .catch((error) => {
      console.error(
        'Failed to read patient before recording inferred appointment date; '
        + 'appending audit entry regardless:',
        error,
      );
      append();
    });
}

/**
 * Apply one or more appointment-date changes in a single Firestore update.
 *
 * Used by the appointment-management dialog so a clinician can edit CI / SSI /
 * DD / PAD in one session and commit them together. That keeps
 * `appointmentDatesLastChangedAt` bumped once and lets downstream report
 * re-finalisation (especially the Patient Report) run once per session.
 *
 * Each change is either a set/update (`newDate` = YYYY-MM-DD) or a remove
 * (`newDate` = null). One audit entry is written per change (fire-and-forget).
 *
 * @param {string} patientId
 * @param {Array<{ moduleKey: string, newDate: string|null, previousDate?: string|null }>} changes
 * @returns {Promise<void>}
 */
export async function applyAppointmentDateChanges(patientId, changes) {
  if (!patientId) {
    throw new Error('patientId is required');
  }
  if (!Array.isArray(changes) || changes.length === 0) {
    throw new Error('At least one appointment date change is required');
  }

  const user = auth.currentUser;
  const patientRef = doc(db, 'patients', patientId);
  const updates = {
    // Single clock bump for the whole batch — PR staleness sees one change.
    appointmentDatesLastChangedAt: serverTimestamp(),
  };
  const auditEntries = [];

  for (const change of changes) {
    const { moduleKey } = change || {};
    if (!VALID_MODULE_KEYS.includes(moduleKey)) {
      throw new Error(`Invalid module key: ${moduleKey}`);
    }

    const previousCalendar = change.previousDate
      ? toCalendarDateString(change.previousDate)
      : null;

    if (change.newDate == null) {
      // Remove the whole module entry (date + metadata).
      updates[`appointmentDates.${moduleKey}`] = deleteField();
      auditEntries.push({
        module: moduleKey,
        action: 'removed',
        trigger: 'manual',
        previousDate: previousCalendar,
        newDate: null,
      });
      continue;
    }

    const calendarDate = toCalendarDateString(change.newDate);
    if (!calendarDate) {
      throw new Error(`Invalid appointment date: ${change.newDate}`);
    }

    updates[`appointmentDates.${moduleKey}.date`] = calendarDate;
    updates[`appointmentDates.${moduleKey}.updatedAt`] = serverTimestamp();
    updates[`appointmentDates.${moduleKey}.updatedBy`] = user?.uid ?? null;
    updates[`appointmentDates.${moduleKey}.updatedByName`] = resolveActorName(user);

    auditEntries.push({
      module: moduleKey,
      action: previousCalendar ? 'updated' : 'set',
      trigger: 'manual',
      previousDate: previousCalendar,
      newDate: calendarDate,
    });
  }

  await updateDoc(patientRef, updates);

  for (const entry of auditEntries) {
    appendAppointmentDateAudit(patientId, entry);
  }
}

/**
 * Write or update an appointment date for a specific assessment module.
 * Thin wrapper around applyAppointmentDateChanges for single-module callers.
 *
 * @param {string} patientId
 * @param {string} moduleKey  One of 'ci', 'ssi', 'dd', 'pad'
 * @param {string} newDate    Calendar date YYYY-MM-DD
 * @param {string|null} previousDate  Previous calendar date (null if first set)
 * @returns {Promise<void>}
 */
export async function setAppointmentDate(patientId, moduleKey, newDate, previousDate = null) {
  await applyAppointmentDateChanges(patientId, [
    { moduleKey, newDate, previousDate },
  ]);
}

/**
 * Remove the appointment date for a specific assessment module.
 * Thin wrapper around applyAppointmentDateChanges for single-module callers.
 *
 * @param {string} patientId
 * @param {string} moduleKey
 * @param {string|null} previousDate  Calendar date that was set before removal
 * @returns {Promise<void>}
 */
export async function removeAppointmentDate(patientId, moduleKey, previousDate) {
  await applyAppointmentDateChanges(patientId, [
    { moduleKey, newDate: null, previousDate },
  ]);
}

/**
 * Fetch the change history for a specific module's appointment dates.
 *
 * Kept for future history-UI re-enablement. Requires a composite index on
 * (module ASC, changedAt DESC) before use in production.
 *
 * @param {string} patientId
 * @param {string} moduleKey
 * @returns {Promise<Array>}
 */
export async function fetchAppointmentDateHistory(patientId, moduleKey) {
  if (!VALID_MODULE_KEYS.includes(moduleKey)) {
    throw new Error(`Invalid module key: ${moduleKey}`);
  }

  const colRef = collection(doc(db, 'patients', patientId), 'appointmentDateHistory');
  const q = query(
    colRef,
    where('module', '==', moduleKey),
    orderBy('changedAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  }));
}

/**
 * Append a single audit document to appointmentDateHistory.
 * Fire-and-forget — failures must not block the primary patient write.
 *
 * Optional `trigger` distinguishes system inference (`moduleCompletion`) from
 * clinician dialog writes (`manual`, or omitted for older entries).
 */
function appendAppointmentDateAudit(patientId, {
  module,
  action,
  previousDate,
  newDate,
  trigger = null,
}) {
  const user = auth.currentUser;
  const colRef = collection(doc(db, 'patients', patientId), 'appointmentDateHistory');

  const payload = {
    module,
    action,
    previousDate: previousDate ?? null,
    newDate: newDate ?? null,
    changedAt: serverTimestamp(),
    changedBy: user?.uid ?? null,
    changedByName: resolveActorName(user),
  };

  // Only include trigger when provided so older/manual entries stay lean.
  if (trigger) {
    payload.trigger = trigger;
  }

  addDoc(colRef, payload)
    .then(() => {
      // Analytics mirror of the audit entry that just landed. Bridged at this
      // one appender because every appointment change - the manual dialog and
      // the auto-set on module completion - already passes through it, so no
      // call site needs to know about the second destination. After the write
      // resolves, never before: an event must not claim a change that failed.
      emitAppointmentDateEvent({
        patientId,
        module,
        action,
        trigger,
        previousDate,
        newDate,
      });
    })
    .catch((err) => {
      console.error('Failed to write appointment date audit entry:', err);
    });
}
